import {
  AppBrokerCredentialError,
  getOpenAnalystApiAccessToken,
  requireOpenAnalystApiAccessToken,
} from "@/context/auth/broker"
import {
  isOpenAnalystHostedBrowserAuthBaseUrl,
  openAnalystBrowserAuthBaseUrl,
  openAnalystMarketplaceBaseUrl,
} from "@/utils/openanalyst-hosts"
import type {
  McpApiResponse,
  McpDisconnectData,
  McpErrorPayload,
  McpFilterOption,
  McpIntegrationCounts,
  McpIntegrationDisplay,
  McpIntegrationsData,
  McpProviderToolsData,
  McpProvidersData,
  McpUserIntegration,
} from "./types"

const MCP_INTEGRATION_BASE_URL = openAnalystMarketplaceBaseUrl()
const MARKETPLACE_BASE_URL = MCP_INTEGRATION_BASE_URL

type CacheEntry<T> = {
  expiresAt: number
  value: Promise<T>
}

const PROVIDERS_TTL_MS = 10 * 60 * 1000
const INTEGRATIONS_TTL_MS = 30 * 1000
const providersCache = new Map<string, CacheEntry<McpProvidersData>>()
const integrationsCache = new Map<string, CacheEntry<McpIntegrationsData>>()

let lastAccessToken: string | null = null
let configuredFetch: typeof fetch | null = null

export type McpRequestAuth = {
  bearerToken?: string | null
}

export function configureMcpFetch(fetcher: typeof fetch | null) {
  configuredFetch = fetcher
  clearMcpApiCache()
}

function localDevAuthBypass() {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | boolean | undefined> }).env ?? {}
  if (!["1", "true"].includes(String(env.DEV)) || env.VITE_REQUIRE_AUTH === "1") return false
  try {
    return !getOpenAnalystApiAccessToken()?.trim()
  } catch {
    return true
  }
}

function mcpUrl(path: string) {
  const hosted = hostedSmartworkMcpUrl(path)
  if (hosted) return hosted
  return `${MCP_INTEGRATION_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`
}

function hostedSmartworkMcpUrl(path: string) {
  const base = openAnalystBrowserAuthBaseUrl()
  if (!isOpenAnalystHostedBrowserAuthBaseUrl(base)) return ""
  if (
    base !== "https://cloud.openanalyst.com" &&
    base !== "https://smartwork.arjun.tv" &&
    base !== "https://smartwork.openanalyst.com"
  ) {
    return ""
  }
  if (path === "/api/integrations?detailed=true") return `${base}/api/v1/mcp/providers`
  if (path === "/api/integrations/connected") return `${base}/api/v1/mcp/integrations`
  return ""
}

function readTtl<T>(cache: Map<string, CacheEntry<T>>, key: string) {
  const entry = cache.get(key)
  if (!entry) return undefined
  if (Date.now() < entry.expiresAt) return entry.value
  cache.delete(key)
  return undefined
}

function cachedTtl<T>(cache: Map<string, CacheEntry<T>>, key: string, ttlMs: number, loader: () => Promise<T>) {
  const value = readTtl(cache, key)
  if (value) return value

  const next = loader().catch((error) => {
    cache.delete(key)
    throw error
  })
  cache.set(key, { expiresAt: Date.now() + ttlMs, value: next })
  return next
}

function syncAccessTokenScope(accessToken = getOpenAnalystApiAccessToken()) {
  if (accessToken !== lastAccessToken) {
    clearMcpApiCache()
    lastAccessToken = accessToken
  }
}

function clearMcpIntegrationCache() {
  integrationsCache.clear()
}

export function clearMcpApiCache() {
  providersCache.clear()
  integrationsCache.clear()
}

async function parseJson(response: Response) {
  const text = await response.text()
  if (!text) return undefined

  try {
    return JSON.parse(text) as unknown
  } catch {
    return { error: text } as McpErrorPayload
  }
}

export class McpApiError extends Error {
  status: number
  code?: string

  constructor(input: { status: number; message: string; code?: string }) {
    super(input.message)
    this.name = "McpApiError"
    this.status = input.status
    this.code = input.code
  }
}

async function toError(response: Response) {
  const payload = (await parseJson(response)) as McpErrorPayload | undefined
  const detailMessage = typeof payload?.detail === "string" ? payload.detail : payload?.detail?.message
  return new McpApiError({
    status: response.status,
    message: payload?.error || detailMessage || `Request failed with status ${response.status}`,
    code: payload?.code,
  })
}

type RequestInput = {
  method?: string
  body?: unknown
  headers?: HeadersInit
  auth?: McpRequestAuth
  openAnalystAccessToken?: string | null
}

async function requestJson(path: string, input?: RequestInput) {
  const headers = new Headers(input?.headers)
  headers.set("Content-Type", "application/json")

  headers.set(
    "Authorization",
    `Bearer ${
      input?.auth?.bearerToken?.trim() ||
      input?.openAnalystAccessToken?.trim() ||
      (await requireOpenAnalystApiAccessToken())
    }`,
  )

  const response = await (configuredFetch ?? fetch)(mcpUrl(path), {
    method: input?.method ?? "GET",
    credentials: hasAuthOverride(input?.auth) ? "omit" : "include",
    headers,
    body: input?.body === undefined ? undefined : JSON.stringify(input.body),
  })

  if (!response.ok) throw await toError(response)

  return parseJson(response)
}

async function request<T>(path: string, input?: RequestInput) {
  const payload = await requestJson(path, input)
  if (payload && typeof payload === "object" && "data" in payload) return (payload as McpApiResponse<T>).data
  return payload as T
}

function providerToolsData(input: unknown): McpProviderToolsData {
  if (input && typeof input === "object" && "data" in input && !("actions" in input)) {
    return (input as { data: McpProviderToolsData }).data
  }
  return input as McpProviderToolsData
}

async function requestProviderTools(path: string, input?: RequestInput) {
  return providerToolsData(await requestJson(path, input))
}

function record(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value)
}

function stringField(value: Record<string, unknown>, key: string) {
  const field = value[key]
  return typeof field === "string" ? field.trim() : ""
}

function statusField(value: string): McpUserIntegration["status"] {
  if (value === "active" || value === "pending" || value === "expired" || value === "revoked") return value
  if (value === "disconnected" || value === "error" || value === "not_connected") return value
  return "not_connected"
}

function normalizeProvider(input: unknown) {
  if (!record(input)) return null
  const details = record(input.provider_details) ? input.provider_details : {}
  const id = stringField(details, "id") || stringField(input, "id") || stringField(input, "provider")
  if (!id) return null
  return {
    id,
    name: stringField(details, "name") || stringField(input, "name") || id,
    description: stringField(details, "description") || stringField(input, "description"),
    category: stringField(details, "category") || stringField(input, "category") || "other",
    icon: stringField(details, "icon") || stringField(input, "icon"),
  }
}

function normalizeProvidersData(input: unknown): McpProvidersData {
  const data = record(input) ? input : {}
  const providers = (
    Array.isArray(data.providers) ? data.providers : Array.isArray(data.integrations) ? data.integrations : []
  )
    .map(normalizeProvider)
    .filter((provider): provider is NonNullable<ReturnType<typeof normalizeProvider>> => !!provider)
  return {
    providers,
    total: typeof data.total === "number" ? data.total : providers.length,
    categories: providers.reduce<Record<string, number>>(
      (categories, provider) => ({
        ...categories,
        [provider.category]: (categories[provider.category] ?? 0) + 1,
      }),
      {},
    ),
  }
}

function normalizeIntegration(input: unknown): McpUserIntegration | null {
  if (!record(input)) return null
  const details = record(input.provider_details) ? input.provider_details : {}
  const provider = stringField(input, "provider") || stringField(input, "id") || stringField(details, "id")
  if (!provider) return null
  return {
    provider,
    status: statusField(stringField(input, "status")),
    connected_email: stringField(input, "connected_email") || null,
    connected_at: stringField(input, "connected_at") || "",
    ...(record(input.provider_details)
      ? { provider_details: normalizeProvider(input.provider_details) ?? undefined }
      : {}),
  } satisfies McpUserIntegration
}

function normalizeIntegrationsData(input: unknown): McpIntegrationsData {
  const data = record(input) ? input : {}
  const integrations = (Array.isArray(data.integrations) ? data.integrations : [])
    .map(normalizeIntegration)
    .filter((integration): integration is McpUserIntegration => !!integration)
  return {
    integrations,
    total: typeof data.total === "number" ? data.total : integrations.length,
  }
}

function hasAuthOverride(auth?: McpRequestAuth) {
  return !!auth?.bearerToken?.trim()
}

export async function getProviders(auth?: McpRequestAuth) {
  if (hasAuthOverride(auth)) return normalizeProvidersData(await request("/api/integrations?detailed=true", { auth }))
  if (localDevAuthBypass()) return { providers: [], total: 0, categories: {} }
  const openAnalystAccessToken = await requireOpenAnalystApiAccessToken()
  syncAccessTokenScope(openAnalystAccessToken)
  return cachedTtl(providersCache, "providers", PROVIDERS_TTL_MS, () =>
    request("/api/integrations?detailed=true", { openAnalystAccessToken }).then(normalizeProvidersData),
  )
}

export async function getIntegrations(auth?: McpRequestAuth) {
  if (hasAuthOverride(auth)) return normalizeIntegrationsData(await request("/api/integrations/connected", { auth }))
  if (localDevAuthBypass()) return { integrations: [], total: 0 }
  const openAnalystAccessToken = await requireOpenAnalystApiAccessToken()
  syncAccessTokenScope(openAnalystAccessToken)
  return cachedTtl(integrationsCache, "integrations", INTEGRATIONS_TTL_MS, () =>
    request("/api/integrations/connected", { openAnalystAccessToken }).then(normalizeIntegrationsData),
  )
}

export async function getProviderTools(provider: string, auth?: McpRequestAuth) {
  const path = `/api/tools/${encodeURIComponent(provider)}/actions?include_schema=false`
  if (hasAuthOverride(auth)) return requestProviderTools(path, { auth })
  if (localDevAuthBypass()) return { provider, actions: [], schema_included: false, total_actions: 0 }
  const openAnalystAccessToken = await requireOpenAnalystApiAccessToken()
  syncAccessTokenScope(openAnalystAccessToken)
  return requestProviderTools(path, { openAnalystAccessToken })
}

export async function disconnectIntegration(provider: string, auth?: McpRequestAuth) {
  const result = await request<McpDisconnectData>("/api/integrations/disconnect", {
    method: "POST",
    body: { provider },
    auth,
  })
  clearMcpIntegrationCache()
  return result
}

export async function getMergedIntegrations(auth?: McpRequestAuth): Promise<McpIntegrationDisplay[]> {
  const [providersData, integrationsData] = await Promise.all([getProviders(auth), getIntegrations(auth)])
  const integrationMap = new Map<string, McpUserIntegration>()

  for (const integration of integrationsData.integrations) {
    integrationMap.set(integration.provider, integration)
  }

  return providersData.providers.map((provider) => {
    const integration = integrationMap.get(provider.id)
    return {
      id: provider.id,
      name: provider.name,
      description: provider.description,
      category: provider.category,
      icon: provider.icon,
      isConnected: integration?.status === "active",
      status: integration?.status ?? "not_connected",
      connectedEmail: integration?.connected_email ?? null,
      connectedAt: integration?.connected_at ?? null,
    }
  })
}

export async function getMarketplaceUrl(providerID?: string): Promise<string | null> {
  const token = await requireOpenAnalystApiAccessToken().catch((error) => {
    if (error instanceof AppBrokerCredentialError && error.code === "missing_openanalyst_session") return null
    throw error
  })
  if (!token) return null

  const baseUrl = MARKETPLACE_BASE_URL.endsWith("/") ? MARKETPLACE_BASE_URL : `${MARKETPLACE_BASE_URL}/`
  const url = new URL(providerID?.trim() ? encodeURIComponent(providerID.trim()) : "", baseUrl)
  url.searchParams.set("token", token)
  return url.toString()
}

export function getCounts(integrations: McpIntegrationDisplay[]): McpIntegrationCounts {
  const connected = integrations.filter((integration) => integration.isConnected).length
  return {
    total: integrations.length,
    connected,
  }
}

export function filterBySearch(integrations: McpIntegrationDisplay[], query: string) {
  if (!query.trim()) return integrations
  const lower = query.toLowerCase()
  return integrations.filter(
    (integration) =>
      integration.name.toLowerCase().includes(lower) || integration.description.toLowerCase().includes(lower),
  )
}

export function filterByStatus(integrations: McpIntegrationDisplay[], filter: McpFilterOption) {
  if (filter === "all") return integrations
  return integrations.filter((integration) => integration.isConnected)
}
